import { useQueryClient } from '@tanstack/react-query'
import { isEmpty, noop } from 'lodash'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import useSignOut from '~/features/auth/hooks/useSignOut'
import { TSessionDetails } from '~/features/auth/models'
import { queries } from '~/features/auth/utils'
import { TPartner } from '~/features/partners/models'
import { queries as partnerQueries } from '~/features/partners/utils'
import useUpdateReferredByCode from '~/features/referrals/hooks/useUpdateReferredByCode'
import useCreateUser from '~/features/user/hooks/useCreateUser'
import useCreateUpdateUser from '~/features/user/hooks/useUpdateUser'
import { TErrorMutateUserResponse, TUser } from '~/features/user/models'
import { queries as userQueries } from '~/features/user/utils'
import { EClientPath, EHttpErrorName } from '~/models/api'
import { HttpServiceBaseResponse } from '~/services/http/models'
import { sessionStore, store } from '~/store'

const useCompleteSignIn = () => {
  const [referredByCode, setUser, impersonatorsUserId, currentUserId] = store(
    (state) => [
      state.user.referredBy,
      state.setUser,
      state.user.impersonatorsUserId,
      state.user.id,
    ],
  )
  const setUIElements = sessionStore((state) => state.setUIElements)

  const { signOut } = useSignOut()

  const queryClient = useQueryClient()

  const location = useLocation()

  const { mutateAsync: createUser } = useCreateUser()
  const { mutate: updateUser } = useCreateUpdateUser()
  const { mutateAsync: updateReferralByCode } = useUpdateReferredByCode()

  const createNewUser = async (
    authSession: TSessionDetails,
  ): Promise<
    HttpServiceBaseResponse<TUser | TErrorMutateUserResponse> | Error
  > => {
    let partner: HttpServiceBaseResponse<TPartner>

    try {
      partner = await queryClient.fetchQuery({
        ...partnerQueries.partnerByDomain(true),
        staleTime: 0,
      })
    } catch (e) {
      toast.error('An error occurred while fetching partner data.')
      console.error('An error occurred while fetching partner data.', 1)
      return new Error('An error occurred while fetching partner data.')
    }

    if (!partner?.data?.id || !authSession?.cognitoUid) {
      toast.error('An error occurred while fetching partner data.')
      console.error('An error occurred while fetching partner data.', 2)
      return new Error('An error occurred while fetching partner data.')
    }

    return createUser(
      {
        cognitoUid: authSession.cognitoUid,
        partnerId: partner.data.id,
        email: authSession.email,
        tempDemoOrg:
          location.state?.previousPathname === EClientPath.partnerAuth,
      },
      {
        onSuccess: (response) => {
          if ('id' in (response?.data || {})) {
            const addedUser = (response.data || {}) as TUser

            setUser({
              id: addedUser.id,
              organizationId: addedUser.orgId,
            })

            if (referredByCode && addedUser.orgId) {
              updateReferralByCode({
                orgId: addedUser.orgId,
                referredByCode,
              })
            }
          }
        },
      },
    )
  }

  /**
   * If the user is not in the database, add them
   * If the user is in the database, update the local store from the response of fetchAuthSession
   */
  const createOrUpdateUser = async () => {
    try {
      let authSession: TSessionDetails | undefined

      try {
        authSession = await queryClient.fetchQuery({
          ...queries.authSession(),
          staleTime: 0,
        })
      } catch (e) {
        toast.error('An error occurred while fetching auth session.')
        console.error('An error occurred while fetching auth session.', 3)
        signOut()
        return
      }

      if (!authSession?.cognitoUid) {
        toast.error('No cognitoUid found in auth session')
        console.error('No cognitoUid found in auth session', 4)
        signOut()
        return
      }

      let userData: HttpServiceBaseResponse<TUser>

      try {
        userData = await queryClient.fetchQuery(
          userQueries.userCognito(authSession.cognitoUid),
        )
      } catch (e: unknown) {
        if ((e as Error).name === EHttpErrorName.userNotFound) {
          setUIElements({ isLoadingUser: true })
          try {
            await createNewUser(authSession)
            userData = await queryClient.fetchQuery(
              userQueries.userCognito(authSession.cognitoUid),
            )
          } catch (_) {
            toast.error('An error occurred while fetching user data.')
            console.error('An error occurred while fetching user data.', 5)
            signOut()
            return
          } finally {
            setUIElements({ isLoadingUser: false })
          }
        } else {
          toast.error('An error occurred while fetching user data.')
          console.error('An error occurred while fetching user data.', 6)
          signOut()
          return
        }
      }

      if (isEmpty(userData.data)) {
        toast.error('An error occurred while fetching user data.')
        console.error('An error occurred while fetching user data.', 7)
        signOut()
        return
      }

      updateUser({
        params: {
          id: userData.data.id,
          // update user with last login as now
          lastLogin: new Date().toISOString(),
        },
      })

      if (
        userData.data.id &&
        userData.data.id !== impersonatorsUserId &&
        userData.data.id !== currentUserId
      ) {
        setUser({
          id: userData.data.id,
          organizationId: userData.data.orgId,
        })
      }
    } catch (e) {
      noop()
    }
  }

  return { completeSignIn: createOrUpdateUser }
}

export default useCompleteSignIn
